import {
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import {
  authErrorSchema,
  authOkSchema,
  authenticationOptionsSchema,
  declinedSchema,
  mergeVerifyResponseSchema,
  registrationOptionsSchema,
  sessionSchema,
  type AuthFailureCode,
  type Session,
} from '../../shared/contracts/auth-api.ts';

export type {
  AuthFailureCode,
  Session,
} from '../../shared/contracts/auth-api.ts';
export class AuthFailure extends Error {
  constructor(readonly code: AuthFailureCode) {
    super(code);
  }
}
async function parseAuthJson<Schema extends z.ZodType>(
  url: string,
  schema: Schema,
  init?: RequestInit,
): Promise<z.output<Schema>> {
  const response = await fetch(url, {
    credentials: 'include',
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!response.ok) {
    let code: AuthFailureCode = 'authentication';
    try {
      const parsed = authErrorSchema.safeParse(await response.json());
      if (parsed.success) code = parsed.data.error;
    } catch {
      // Preserve the generic authentication failure for non-JSON errors.
    }
    throw new AuthFailure(code);
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new AuthFailure('authentication');
  }
  const parsed = schema.safeParse(body);
  if (!parsed.success) throw new AuthFailure('authentication');
  return parsed.data;
}
async function register() {
  const options = await parseAuthJson(
    '/api/auth/passkey/register/options',
    registrationOptionsSchema,
    {
      method: 'POST',
      body: '{}',
    },
  );
  const response = await startRegistration({ optionsJSON: options });
  return parseAuthJson('/api/auth/passkey/register/verify', authOkSchema, {
    method: 'POST',
    body: JSON.stringify({
      challenge: options.challenge,
      response,
    }),
  });
}
async function signUp(locale: string) {
  const options = await parseAuthJson(
    '/api/auth/passkey/signup/options',
    registrationOptionsSchema,
    {
      method: 'POST',
      body: JSON.stringify({ locale }),
    },
  );
  const response = await startRegistration({ optionsJSON: options });
  return parseAuthJson('/api/auth/passkey/signup/verify', authOkSchema, {
    method: 'POST',
    body: JSON.stringify({ challenge: options.challenge, response }),
  });
}
async function signIn() {
  const options = await parseAuthJson(
    '/api/auth/passkey/login/options',
    authenticationOptionsSchema,
    {
      method: 'POST',
      body: '{}',
    },
  );
  const response = await startAuthentication({ optionsJSON: options });
  return parseAuthJson('/api/auth/passkey/login/verify', authOkSchema, {
    method: 'POST',
    body: JSON.stringify({ challenge: options.challenge, response }),
  });
}
async function mergeOptions() {
  return parseAuthJson(
    '/api/auth/passkey/merge/options',
    authenticationOptionsSchema,
    { method: 'POST', body: '{}' },
  );
}
async function mergeVerify(
  challenge: string,
  response: Awaited<ReturnType<typeof startAuthentication>>,
) {
  return parseAuthJson(
    '/api/auth/passkey/merge/verify',
    mergeVerifyResponseSchema,
    {
      method: 'POST',
      body: JSON.stringify({ challenge, response }),
    },
  );
}
export const authClient = {
  useSession() {
    const [data, setData] = useState<Session | null>(null);
    const [isPending, setPending] = useState(true);
    const refetch = useCallback(async () => {
      setPending(true);
      try {
        const response = await fetch('/api/session', {
          credentials: 'include',
        });
        setData(
          response.ok ? sessionSchema.parse(await response.json()) : null,
        );
      } finally {
        setPending(false);
      }
    }, []);
    useEffect(() => {
      void refetch().catch(() => undefined);
    }, [refetch]);
    return { data, isPending, refetch };
  },
  passkey: {
    addPasskey: () => register(),
    mergeOptions,
    mergeVerify,
    mergeConfirm: (token: string) =>
      parseAuthJson('/api/auth/passkey/merge/confirm', authOkSchema, {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),
    mergeDecline: (token: string) =>
      parseAuthJson('/api/auth/passkey/merge/decline', declinedSchema, {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),
  },
  exchangeTelegramToken: (token: string) =>
    parseAuthJson('/api/auth/telegram/exchange', authOkSchema, {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  mergeTelegramAccount: (token: string) =>
    parseAuthJson('/api/auth/telegram/merge', authOkSchema, {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  signIn: { passkey: signIn },
  signUp: { passkey: signUp },
  signOut: () =>
    parseAuthJson('/api/auth/sign-out', authOkSchema, {
      method: 'POST',
      body: '{}',
    }),
};
